import React from "react";

import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Link, useLocation } from "react-router-dom";

import { WrappedLink } from "@components/navigation";
import { LicensingIconsObj } from "@helpers/shared/LicensingIcons/licensingIcons";
import { useRootAppStore } from "@hooks";
import { useAppContext } from "@state/hooks/shared";
import { THEME_SETTINGS } from "@theme-settings";
import { getCurrentCulture, isGamesTheme } from "@utils";
import { isLoginFlow } from "@utils/membership/membershipUtils";
import { useNotification } from "@utils/notifications";
import * as Cookie from "vanilla-cookieconsent";

import { ScrollToTop } from "../../../../footer/desktop";
import {
	FooterOwned,
	PaymentOptions,
} from "../../../../footer/desktop/components";

export function LayoutFooter(props: { footerClassName: string }) {
	const { t } = useTranslation(["translation", "complaints"]);
	const { culture } = useAppContext();
	const { pathname } = useLocation();
	const rootStore = useRootAppStore();
	const { showInfo } = useNotification();

	const { unreadSupportMessages } = rootStore.chatViewStore;
	const { isLoggedIn } = rootStore.userAuthStore;

	const date = new Date();

	if (isLoginFlow(pathname)) {
		return null;
	}

	return (
		<footer className={props.footerClassName}>
			<div className="footer">
				<div className="footer__section">
					<div className="row">
						<div className="col col-sml-12 col-med-12 col-lrg-6 col-xlrg-3">
							<div className="footer__title">
								{t<string>("FOOTER.COMPANY")}
							</div>
							<nav>
								<ul>
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/about-us`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.ABOUTUS")}
										</WrappedLink>
									</li>
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/contact`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.CONTACT")}
										</WrappedLink>
									</li>
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/complaints`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("complaints:TITLE")}
										</WrappedLink>
									</li>
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/our-partners`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.OURPARTNERS")}
										</WrappedLink>
									</li>
									{!isGamesTheme() && (
										<li>
											<a
												className="u-anchor u-anchor--underline u-anchor--primary"
												href="https://franchise.tipwin.com/"
												target="_blank"
											>
												{t<string>("FOOTER.FRANCHISE")}
											</a>
										</li>
									)}
									<li className="u-mar--top--xtny">
										<a
											className="u-anchor u-anchor--underline u-anchor--primary"
											href="https://affiliate.tipwin.com/"
										>
											{t<string>("FOOTER.AFFILIATE")}
										</a>
									</li>
								</ul>
							</nav>
						</div>
						<div className="col col-sml-12 col-med-12 col-lrg-6 col-xlrg-3">
							<div className="footer__title">
								{t<string>("FOOTER.INFORMATION")}
							</div>
							<nav>
								<ul>
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/terms-and-conditions`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.TAC")}
										</WrappedLink>
									</li>
									{!isGamesTheme() && (
										<li className="u-mar--bottom--xtny">
											<WrappedLink
												to={`/${culture}/info/bonus-terms`}
												className="u-anchor u-anchor--secondary"
											>
												{t<string>(
													"FOOTER.BONUS_TERMS"
												)}
											</WrappedLink>
										</li>
									)}
									{!isGamesTheme() && (
										<li className="u-mar--bottom--xtny">
											<WrappedLink
												to={`/${culture}/info/freebet-terms`}
												className="u-anchor u-anchor--secondary"
											>
												{t<string>(
													"FOOTER.FREEBET_TERMS"
												)}
											</WrappedLink>
										</li>
									)}
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/privacy`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.PRIVACY")}
										</WrappedLink>
									</li>
									<button
										type="button"
										className="btn btn--reset btn--ghost u-color--text--secondary u-anchor u-anchor--secondary u-mar--bottom--xtny"
										onClick={() => {
											Cookie.showPreferences();
										}}
									>
										{t("FOOTER.COOKIE_POLICY")}
									</button>
									{THEME_SETTINGS.isWhistleblowingEnabled && (
										<li>
											<WrappedLink
												to={`/${culture}/info/whistleblowing-channel`}
												className="u-anchor u-anchor--secondary"
											>
												{t<string>(
													"FOOTER.WHISTLEBLOWING_CHANNEL"
												)}
											</WrappedLink>
										</li>
									)}
								</ul>
							</nav>
						</div>
						<div className="col col-sml-12 col-med-12 col-lrg-6 col-xlrg-3">
							<div className="footer__title">
								{t<string>("FOOTER.HELP")}
							</div>
							<nav>
								<ul>
									{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE && (
										<li className="u-mar--bottom--xtny">
											<WrappedLink
												onClick={(e) => {
													if (!isLoggedIn) {
														e.preventDefault();
														showInfo(
															t(
																"CHAT.ERR_HANDLING.PLEASE_LOGIN"
															)
														);
													}
												}}
												to={`/${culture}/support-chat`}
												className="u-anchor u-anchor--secondary"
											>
												<i className="u-icon u-icon--base u-icon--support--alt u-mar--right--tny">
													{unreadSupportMessages && (
														<span className="tag tag--dot tag--dot--smoke" />
													)}
												</i>
												<span className="u-align--v--middle u-mar--right--tny">
													{t("CHAT.SUPPORT.TITLE")}
												</span>
											</WrappedLink>
										</li>
									)}
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/help`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>("FOOTER.HELP")}
										</WrappedLink>
									</li>
									{!isGamesTheme() &&
										THEME_SETTINGS.isBettingRulesEnabled && (
											<li className="u-mar--bottom--xtny">
												<Link
													to={`/${culture}/info/betting-rules`}
													className="u-anchor u-anchor--secondary"
												>
													{t<string>(
														"FOOTER.BET_RULES"
													)}
												</Link>
											</li>
										)}
									{WEBPACK_IS_CASINO_LIVE_ENABLED ? (
										<>
											<li className="u-mar--bottom--xtny">
												<WrappedLink
													to={`/${culture}/info/live-casino-terms`}
													className="u-anchor u-anchor--secondary"
												>
													{t<string>(
														"FOOTER.LIVE_CASINO_TERMS"
													)}
												</WrappedLink>
											</li>
											<li className="u-mar--bottom--xtny">
												<WrappedLink
													to={`/${culture}/info/live-casino-rules`}
													className="u-anchor u-anchor--secondary"
												>
													{t<string>(
														"FOOTER.LIVE_CASINO_RULES"
													)}
												</WrappedLink>
											</li>
										</>
									) : null}
									<li className="u-mar--bottom--xtny">
										<WrappedLink
											to={`/${culture}/info/responsible-gaming`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>(
												"FOOTER.RESPONSIBLE_GAMING"
											)}
										</WrappedLink>
									</li>
									<li className="u-mar--top--xtny">
										<WrappedLink
											to={`/${culture}/info/payment-options`}
											className="u-anchor u-anchor--secondary"
										>
											{t<string>(
												"FOOTER.PAYMENT_OPTIONS"
											)}
										</WrappedLink>
									</li>
									{THEME_SETTINGS.isPlayerMoneyProtectionEnabled && (
										<li className="u-mar--top--xtny">
											<WrappedLink
												to={`/${culture}/info/player-money-protection`}
												className="u-anchor u-anchor--secondary"
											>
												{t<string>(
													"specific:FOOTER.PLAYER_MONEY_PROTECTION"
												)}
											</WrappedLink>
										</li>
									)}
								</ul>
							</nav>
						</div>
						<div className="col col-sml-12 col-med-12 col-lrg-6 col-xlrg-3">
							<div className="footer__title">
								{t<string>("FOOTER.FOLLOWUS")}
							</div>
							<nav>
								<ul>
									<li className="u-mar--bottom--xtny">
										<a
											href="https://www.facebook.com/TipwinSport/"
											className="u-anchor u-anchor--secondary"
											target="_blank"
										>
											<i className="u-icon u-icon--base u-icon--facebook u-mar--right--tny" />
											{t<string>("Facebook")}
										</a>
									</li>
									<li className="u-mar--bottom--xtny">
										<a
											href="https://twitter.com/TipWinCompany"
											className="u-anchor u-anchor--secondary"
											target="_blank"
										>
											<i className="u-icon u-icon--base u-icon--twitter u-mar--right--tny" />
											{t<string>("Twitter")}
										</a>
									</li>
									<li className="u-mar--bottom--xtny">
										<a
											href="https://www.instagram.com/tipwinofficial/"
											className="u-anchor u-anchor--secondary"
											target="_blank"
										>
											<i className="u-icon u-icon--base u-icon--instagram u-mar--right--tny" />
											{t<string>("Instagram")}
										</a>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				</div>

				<div className="footer__section footer__section--bordered">
					<div className="row">
						<div className="footer__col--security col col-sml-12 col-med-12 col-lrg-12 col-xlrg-5">
							<div className="footer__title">
								{t<string>("FOOTER.SECURITY_AND_TRUST")}
							</div>
							<div className="footer__icons">
								<i
									className="footer__icons__item u-icon u-icon--bv-iso-cert"
									data-cert-no="IND17.0542/U"
								/>
								{THEME_SETTINGS.licensingIcons.map(
									(icon: keyof typeof LicensingIconsObj) => (
										<a
											key={icon}
											href={LicensingIconsObj[icon].url}
											className={`footer__icons__item footer__icons__${LicensingIconsObj[icon].icon}`}
											rel="nofollow"
											title={
												LicensingIconsObj[icon].title
											}
											target="_blank"
										>
											<i
												className={`u-icon u-icon--${LicensingIconsObj[icon].icon}`}
											/>
										</a>
									)
								)}
							</div>
						</div>

						<div className="footer__col--payment col col-sml-12 col-med-12 col-lrg-12 col-xlrg-4">
							<PaymentOptions />
						</div>

						<div className="footer__col--partners col col-sml-12 col-med-12 col-lrg-12 col-xlrg-3">
							<div className="footer__title">
								{t<string>("FOOTER.OURPARTNERS")}
							</div>
							<a
								className="footer__partner u-mar--bottom--xsml"
								title="Bayer 04 Leverkusen"
								href={`/${getCurrentCulture()}/info/our-partners`}
							>
								{/* <i className="footer__partner__icon u-icon u-icon--xxxlrg u-icon--partner u-icon--partner--bayer" /> */}
								<img
									className="footer__partner__img"
									src="/assets/images/footer/bayer-04-leverkusen-x-tipwin--vertical--cropped.png"
									alt="Bayer 04 Leverkusen"
								/>
								<div className="footer__partner__text">
									<div className="u-type--xxsml u-color--text--tertiary">
										{t<string>("FOOTER.PREMIUM_PARTNER")}
									</div>
									<div className="u-type--xxsml">
										BAYER 04 LEVERKUSEN
									</div>
								</div>
							</a>
							<a
								href={`/${getCurrentCulture()}/info/our-partners`}
								className="footer__partner"
								title={t("specific:FOOTER.DBB_NAME")}
							>
								<i className="footer__partner__icon u-icon u-icon--huge u-icon--partner u-icon--partner--dbb" />
								<div className="footer__partner__text">
									<div className="u-type--xxsml u-color--text--tertiary">
										{t<string>("FOOTER.PREMIUM_PARTNER")}
									</div>
									<div className="u-type--xxsml">
										{t<string>("specific:FOOTER.DBB_NAME")}
									</div>
								</div>
							</a>
						</div>
					</div>
				</div>

				<div className="footer__section footer__section--bordered u-type--xxsml">
					{!isGamesTheme() && (
						<div className="footer__legal">
							<div className="footer__legal__icons">
								{THEME_SETTINGS.licensingIcons.includes(
									"ggl"
								) && (
									<div className="footer__legal__thumb">
										<a
											href="https://www.gluecksspiel-behoerde.de/de/"
											className="footer__icons__item"
											rel="nofollow"
											title="GGL"
											target="_blank"
										>
											<i className="u-icon u-icon--ggl" />
										</a>
									</div>
								)}

								<i className="footer__icons__item u-icon u-icon--huge u-icon--age-restriction u-color--sent--danger" />
							</div>

							<div className="footer__legal__content">
								<div className="footer__legal__inline">
									<span className="u-mar--right--micro u-type--wgt--bold">
										{t<string>("FOOTER.WARNING_LABEL")}
									</span>
									<ReactMarkdown
										linkTarget={"blank"}
										className="u-anchor--nested"
									>
										{t<string>(
											"specific:FOOTER.WARNING_TEXT"
										)}
									</ReactMarkdown>
								</div>
							</div>
						</div>
					)}

					{isGamesTheme() && (
						<div className="footer__legal">
							<div className="footer__legal__icons">
								<div className="footer__legal__thumb">
									<a
										href="https://www.gluecksspiel-behoerde.de/de/"
										className="footer__icons__item"
										rel="nofollow"
										title="GGL"
										target="_blank"
									>
										<i className="u-icon u-icon--ggl" />
									</a>
								</div>

								<i className="footer__icons__item u-icon u-icon--huge u-icon--age-restriction u-color--sent--danger" />
							</div>

							<div className="footer__legal__content">
								<div className="footer__legal__inline">
									<span className="u-mar--right--micro u-type--wgt--bold">
										{t<string>("FOOTER.WARNING_LABEL")}
									</span>
									<ReactMarkdown
										linkTarget={"blank"}
										className="u-anchor--nested"
									>
										{t("FOOTER.WARNING_TEXT_GAMES")}
									</ReactMarkdown>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="footer__section footer__section--bordered u-type--xxsml">
					<FooterOwned />

					<div>
						<ReactMarkdown
							linkTarget={"_blank"}
							className="u-anchor--nested"
						>
							{t("FOOTER.PROTECTED_GOOGLE_INFO")}
						</ReactMarkdown>
					</div>
				</div>

				<div className="footer__section footer__section--bordered u-type--xxsml u-type--center">
					&#169;
					{t<string>("specific:FOOTER.ALL_RIGHTS_RESERVED", {
						0: date.getFullYear(),
					})}{" "}
					{t<string>("specific:FOOTER.FOOTER_RIGHTS_RESERVED")}
				</div>
			</div>

			<ScrollToTop />
		</footer>
	);
}
